import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from '@config/constants';
import {
  IJobOrder,
  IJobOrderDataState,
  IJobOrderDetail,
} from 'app/models/job-order.model';
import { forkJoin, Observable } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import { DataStateService } from '../data-state/data.state.service';

import { ErrorHandlingService } from '../error-handling/error.handling.service';
import { MachineService } from '../machine/machine.service';
import { AuthenticationTokenStorageService } from '../session-storage/authentication.token.storage.service';

@Injectable({
  providedIn: 'root',
})
export class JobOrderService {
  constructor(
    private readonly http: HttpClient,
    private readonly constants: Constants,
    private readonly errorHandlingService: ErrorHandlingService,
    private readonly machineService: MachineService,
    private readonly dataStateService: DataStateService,
    private readonly authenticationTokenStorageService: AuthenticationTokenStorageService
  ) { }

  getAllJobs(filters?: {
    code?: string;
    description?: string;
    finalCustomer?: string;
    from?: Date;
    to?: Date;
  }): Observable<IJobOrder[]> {
    let params = new HttpParams();
    const { customerId } =
      this.authenticationTokenStorageService.getLoggedUser();
    params = params.set('customerId', customerId.toString());

    if (filters?.code) {
      params = params.set('code', filters.code);
    }

    if (filters?.description) {
      params = params.set('description', filters.description);
    }

    if (filters?.finalCustomer) {
      params = params.set('finalCustomer', filters.finalCustomer);
    }

    if (filters?.from) {
      params = params.set('from', filters.from.toISOString());
    }

    if (filters?.to) {
      params = params.set('to', filters.to.toISOString());
    }

    return this.http
      .get<IJobOrder[]>(this.constants.JOB_ORDER_ENDPOINT, { params })
      .pipe(
        retry(this.constants.HTTP_REQUEST_RETRY_TIMES),
        catchError(this.errorHandlingService.handleHttpError)
      );
  }

  getJobOrder(jobId: number): Observable<IJobOrder> {
    return this.http
      .get<IJobOrder>(`${this.constants.JOB_ORDER_ENDPOINT}/${jobId}`)
      .pipe(
        retry(this.constants.HTTP_REQUEST_RETRY_TIMES),
        catchError(this.errorHandlingService.handleHttpError)
      );
  }

  getJobOrderOrderDetail(jobId: number): Observable<IJobOrderDetail> {
    return forkJoin([
      this.getJobOrder(jobId),
      this.getJobOrderOrderGanttData(jobId),
      this.getJobOrderOrderHistogramData(jobId),
    ]).pipe(
      map(([jobOrder, chartData, histogramData]) => ({
        ...jobOrder,
        data: [].concat.apply(
          [],
          chartData.map((section: any) =>
            section.data.map((entry: any) => [
              entry[2],
              new Date(entry[0]),
              new Date(entry[1]),
            ])
          )
        ),
        histogramData: histogramData.map((entry: { x: string; y: number }) => ({ label: entry.x, value: entry.y })),
      }))
    );
  }

  getJobOrderOrderGanttData(jobId: number): Observable<any> {
    return this.http.get<any>(
      `${this.constants.JOB_ORDER_ENDPOINT}/${jobId}/gantt`
    );
  }

  getJobOrderOrderHistogramData(jobId: number): Observable<any> {
    return this.http.get<any>(
      `${this.constants.JOB_ORDER_ENDPOINT}/${jobId}/histogram`
    );
  }

  createJobOrder(jobOrder: IJobOrder): Observable<IJobOrder> {
    const { customerId } =
      this.authenticationTokenStorageService.getLoggedUser();

    return this.http.post<IJobOrder>(this.constants.JOB_ORDER_ENDPOINT, {
      ...jobOrder,
      customerId,
    });
  }

  updateJobOrder(
    jobOrder: IJobOrder,
    dataStateKeys: any[]
  ): Observable<IJobOrder> {
    const { customerId } =
      this.authenticationTokenStorageService.getLoggedUser();
    return this.http
      .patch<IJobOrder>(`${this.constants.JOB_ORDER_ENDPOINT}/${jobOrder.id}`, {
        ...jobOrder,
        customerId,
        dataStateKeys,
      })
      .pipe(
        retry(this.constants.HTTP_REQUEST_RETRY_TIMES),
        catchError(this.errorHandlingService.handleHttpError)
      );
  }

  getDataStatesForJobOrder(jobId: number): Observable<IJobOrderDataState[]> {
    const { customerId } =
      this.authenticationTokenStorageService.getLoggedUser();
    return forkJoin([
      this.machineService.findAllMachinesByCustomer(customerId),
      this.getJobOrder(jobId),
    ]).pipe(
      map(([machines, jobOrder]) =>
        jobOrder.relatedDataStates.map((dataState) => {
          const machine = machines.find((m) => m.id === dataState.machineId);
          const state: IJobOrderDataState = {
            ...dataState,
            id: dataState.dateTimeEventAcquisitionISO,
            jobOrderIds: [jobOrder.id],
            description: dataState.description ?? '',
            operation: machine?.function ?? '',
            machineName: machine?.name ?? '',
            startDate: dataState.dateTimeState ?? '',
          };

          return state;
        })
      )
    );
  }

  getJobOrderDataStates(filters?: {
    description?: string;
    from?: Date;
    to?: Date;
  }): Observable<IJobOrderDataState[]> {
    const { customerId } =
      this.authenticationTokenStorageService.getLoggedUser();

    return forkJoin([
      this.machineService.findAllMachinesByCustomer(customerId),
      this.dataStateService.getAllMachineStates(
        customerId,
        undefined,
        filters?.description,
        filters?.from,
        filters?.to,
        'RUN'
      ),
      this.getAllJobs(),
    ]).pipe(
      map(([machines, dataStates, jobOrders]) =>
        dataStates.map((dataState) => {
          const machine = machines.find((m) => m.id === dataState.machineId);
          const state: IJobOrderDataState = {
            ...dataState,
            id: dataState.dateTimeEventAcquisitionISO,
            jobOrderIds: jobOrders
              // This is required cause data_states elements fetched from dedicated endpoint doesn't
              // contain the job ids they are linked to.
              .map((jobOrder) => {
                if (
                  jobOrder.relatedDataStates.find(
                    (relatedDataState) =>
                      relatedDataState.dateTimeEventAcquisitionISO ===
                      dataState.dateTimeEventAcquisitionISO
                  )
                ) {
                  return jobOrder.id;
                }
                return undefined;
              })
              .filter((elem) => elem !== undefined) as number[],
            description: dataState.description ?? '',
            operation: machine?.function ?? '',
            machineName: machine?.name ?? '',
            startDate: dataState.dateTimeState ?? '',
          };

          return state;
        })
      )
    );
  }

  deleteJobOrder(jobId: number): Observable<any> {
    return this.http.delete(`${this.constants.JOB_ORDER_ENDPOINT}/${jobId}`);
  }

  downloadJobOrderCSV(jobId: number): any {
    return this.http.get(`${this.constants.JOB_ORDER_ENDPOINT}/${jobId}/csv`, { responseType: 'blob' });
  }

  downloadJobOrderDataStatesCSV(jobId: number): any {
    return this.http.get(`${this.constants.JOB_ORDER_ENDPOINT}/${jobId}/csv-data-states`, { responseType: 'blob' });
  }
}
